<template>
  <div>
    <!-- Filters Card -->
    <v-card class="mb-4">
      <v-card-title class="pb-1">
        <v-icon color="primary" class="me-2">mdi-filter-variant</v-icon>
        <span class="text-subtitle-1 font-weight-bold">{{ $t('Filters') || 'Filters' }}</span>
        <v-spacer></v-spacer>
        <v-btn color="grey lighten-2" class="text-capitalize" small @click="resetFilters">
          <v-icon left size="16">mdi-filter-off</v-icon>
          {{ $t('Reset Filters') || 'Reset' }}
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row class="mt-1">
          <!-- User Search / Pass Code -->
          <v-col cols="12" sm="4" md="3" class="py-1">
            <v-text-field
              v-model="filters.user_search"
              :label="$t('Search customer/pass code') || 'Customer Search'"
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              @input="onFilterChange"
            ></v-text-field>
          </v-col>

          <!-- DB Status -->
          <v-col cols="12" sm="4" md="3" class="py-1">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              :label="$t('Status') || 'Status'"
              outlined
              dense
              hide-details
              clearable
              @change="onFilterChange"
            ></v-select>
          </v-col>

          <!-- Payment Status -->
          <v-col cols="12" sm="4" md="3" class="py-1">
            <v-select
              v-model="filters.payment_status"
              :items="paymentStatusOptions"
              :label="$t('Payment Status') || 'Payment Status'"
              outlined
              dense
              hide-details
              clearable
              @change="onFilterChange"
            ></v-select>
          </v-col>

          <!-- Computed Status -->
          <v-col cols="12" sm="4" md="3" class="py-1">
            <v-select
              v-model="filters.computed_status"
              :items="statusOptions"
              :label="$t('Computed Status') || 'Computed Status'"
              outlined
              dense
              hide-details
              clearable
              @change="onFilterChange"
            ></v-select>
          </v-col>

          <!-- Plan Dropdown -->
          <v-col cols="12" sm="4" md="3" class="py-1">
            <v-select
              v-model="filters.plan_id"
              :items="plans"
              item-text="name"
              item-value="id"
              :label="$t('Plan') || 'Plan'"
              outlined
              dense
              hide-details
              clearable
              @change="onFilterChange"
            ></v-select>
          </v-col>

          <!-- Route Dropdown -->
          <v-col cols="12" sm="4" md="3" class="py-1">
            <v-select
              v-model="filters.route_id"
              :items="routes"
              item-text="name"
              item-value="id"
              :label="$t('Route') || 'Route'"
              outlined
              dense
              hide-details
              clearable
              @change="onFilterChange"
            ></v-select>
          </v-col>

          <!-- Date From -->
          <v-col cols="12" sm="4" md="3" class="py-1">
            <v-text-field
              v-model="filters.date_from"
              :label="$t('Start Date From') || 'Start Date From'"
              type="date"
              outlined
              dense
              hide-details
              clearable
              @input="onFilterChange"
            ></v-text-field>
          </v-col>

          <!-- Date To -->
          <v-col cols="12" sm="4" md="3" class="py-1">
            <v-text-field
              v-model="filters.date_to"
              :label="$t('End Date To') || 'End Date To'"
              type="date"
              outlined
              dense
              hide-details
              clearable
              @input="onFilterChange"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Main Data Table Card -->
    <v-card>
      <v-card-title>
        <v-icon color="primary" class="me-2">mdi-card-account-details-outline</v-icon>
        <span class="px-2">{{ $t('Route Passes') || 'Route Subscription Passes' }}</span>
        <v-spacer></v-spacer>
      </v-card-title>

      <v-data-table
        :headers="translatedHeaders"
        :items="passes"
        :options.sync="options"
        :server-items-length="totalPasses"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :no-data-text="$t('no_data_available')"
        :footer-props="footerProps"
        class="elevation-0"
      >
        <!-- Pass Code -->
        <template v-slot:item.pass_code="{ item }">
          <span class="font-weight-bold text-mono">{{ item.pass_code }}</span>
        </template>

        <!-- Customer -->
        <template v-slot:item.customer="{ item }">
          <div v-if="item.user">
            <div class="font-weight-semibold">{{ item.user.name }}</div>
            <div class="text-caption text--secondary">{{ item.user.tel_number || item.user.email }}</div>
          </div>
          <span v-else class="text--secondary">-</span>
        </template>

        <!-- Plan -->
        <template v-slot:item.plan="{ item }">
          <span v-if="item.plan">
            {{ $i18n.locale === 'ar' && item.plan.name_ar ? item.plan.name_ar : item.plan.name_en }}
          </span>
          <span v-else class="text--secondary">-</span>
        </template>

        <!-- Route -->
        <template v-slot:item.route="{ item }">
          <span v-if="item.route">{{ item.route.name }}</span>
          <span v-else class="text--secondary">-</span>
        </template>

        <!-- Segment -->
        <template v-slot:item.segment="{ item }">
          <div v-if="item.pickup_stop && item.dropoff_stop" class="text-caption">
            <v-icon size="14" color="success">mdi-map-marker-radius</v-icon>
            {{ item.pickup_stop.name }}
            <br />
            <v-icon size="14" color="error">mdi-map-marker-check</v-icon>
            {{ item.dropoff_stop.name }}
          </div>
          <span v-else class="text--secondary">-</span>
        </template>

        <!-- Database Status -->
        <template v-slot:item.status="{ item }">
          <v-chip :color="getStatusColor(item.status)" x-small dark>
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <!-- Payment Status -->
        <template v-slot:item.payment_status="{ item }">
          <v-chip :color="getPaymentStatusColor(item.payment_status)" x-small dark>
            {{ getPaymentStatusText(item.payment_status) }}
          </v-chip>
        </template>

        <!-- Computed Status -->
        <template v-slot:item.computed_status="{ item }">
          <v-chip :color="getStatusColor(item.computed_status)" x-small dark outlined class="font-weight-bold">
            {{ getStatusText(item.computed_status) }}
          </v-chip>
        </template>

        <!-- Rides Used/Limit -->
        <template v-slot:item.rides_used_limit="{ item }">
          <span>{{ item.rides_used }} / {{ item.rides_limit }}</span>
        </template>

        <!-- Rides Remaining -->
        <template v-slot:item.rides_remaining="{ item }">
          <v-chip :color="item.rides_remaining > 0 ? 'success' : 'default'" x-small outlined>
            {{ item.rides_remaining }}
          </v-chip>
        </template>

        <!-- Total Price -->
        <template v-slot:item.total_price="{ item }">
          <span class="font-weight-bold text-success">{{ item.total_price }}</span>
        </template>

        <!-- Start Date -->
        <template v-slot:item.starts_at="{ item }">
          <span class="text-caption">{{ item.starts_at || '-' }}</span>
        </template>

        <!-- End Date -->
        <template v-slot:item.ends_at="{ item }">
          <span class="text-caption">{{ item.ends_at || '-' }}</span>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon color="primary" small v-bind="attrs" v-on="on" @click="showDetails(item)">
                <v-icon size="18">mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('View Details') || 'View Details' }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon color="success" small v-bind="attrs" v-on="on" @click="showUsages(item)">
                <v-icon size="18">mdi-history</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('View Usages') || 'View Usages' }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Details Modal -->
    <v-dialog v-model="detailsDialog" max-width="750px">
      <v-card v-if="selectedPass">
        <v-card-title class="headline primary white--text py-3">
          <v-icon dark class="me-2">mdi-card-text-outline</v-icon>
          {{ $t('Pass Details') || 'Subscription Pass Details' }}
          <v-spacer></v-spacer>
          <span class="text-subtitle-1 font-weight-bold white--text">{{ selectedPass.pass_code }}</span>
          <v-btn icon dark small class="ms-4" @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row>
            <!-- User Info Section -->
            <v-col cols="12" md="6" class="py-2">
              <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-1">
                <v-icon size="16" class="me-1">mdi-account</v-icon>
                {{ $t('Customer Information') || 'Customer Information' }}
              </div>
              <v-divider class="mb-2"></v-divider>
              <div v-if="selectedPass.user">
                <div><strong>{{ $t('Name') || 'Name' }}:</strong> {{ selectedPass.user.name }}</div>
                <div><strong>{{ $t('Email') || 'Email' }}:</strong> {{ selectedPass.user.email || '-' }}</div>
                <div><strong>{{ $t('Phone') || 'Phone' }}:</strong> {{ selectedPass.user.tel_number || '-' }}</div>
              </div>
              <div v-else class="text--secondary">{{ $t('Unknown User') || 'Unknown User' }}</div>
            </v-col>

            <!-- Pass Info Section -->
            <v-col cols="12" md="6" class="py-2">
              <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-1">
                <v-icon size="16" class="me-1">mdi-information-outline</v-icon>
                {{ $t('Pass Information') || 'Pass Information' }}
              </div>
              <v-divider class="mb-2"></v-divider>
              <div>
                <strong>{{ $t('Status') || 'Database Status' }}:</strong>
                <v-chip :color="getStatusColor(selectedPass.status)" x-small dark class="ms-1">
                  {{ getStatusText(selectedPass.status) }}
                </v-chip>
              </div>
              <div class="mt-1">
                <strong>{{ $t('Computed Status') || 'Computed Status' }}:</strong>
                <v-chip :color="getStatusColor(selectedPass.computed_status)" x-small dark outlined class="ms-1 font-weight-bold">
                  {{ getStatusText(selectedPass.computed_status) }}
                </v-chip>
              </div>
              <div class="mt-1">
                <strong>{{ $t('Payment Status') || 'Payment Status' }}:</strong>
                <v-chip :color="getPaymentStatusColor(selectedPass.payment_status)" x-small dark class="ms-1">
                  {{ getPaymentStatusText(selectedPass.payment_status) }}
                </v-chip>
              </div>
              <div class="mt-1">
                <strong>{{ $t('Validity') || 'Validity' }}:</strong>
                {{ selectedPass.starts_at }} / {{ selectedPass.ends_at }}
              </div>
            </v-col>

            <!-- Plan & Pricing -->
            <v-col cols="12" md="6" class="py-2">
              <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-1">
                <v-icon size="16" class="me-1">mdi-clipboard-list-outline</v-icon>
                {{ $t('Plan Info') || 'Plan Info' }}
              </div>
              <v-divider class="mb-2"></v-divider>
              <div v-if="selectedPass.plan">
                <div><strong>{{ $t('Plan') || 'Plan Name' }}:</strong> {{ $i18n.locale === 'ar' && selectedPass.plan.name_ar ? selectedPass.plan.name_ar : selectedPass.plan.name_en }}</div>
                <div><strong>{{ $t('Period Type') || 'Period Type' }}:</strong> {{ $t(selectedPass.plan.period_type) || selectedPass.plan.period_type }}</div>
                <div><strong>{{ $t('Duration') || 'Duration' }}:</strong> {{ selectedPass.plan.duration_days }} {{ $t('Days') || 'days' }}</div>
              </div>
              <div v-else class="text--secondary">-</div>

              <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-1 mt-4">
                <v-icon size="16" class="me-1">mdi-chart-pie</v-icon>
                {{ $t('Rides Summary') || 'Rides Summary' }}
              </div>
              <v-divider class="mb-2"></v-divider>
              <div><strong>{{ $t('Rides Limit') || 'Rides Limit' }}:</strong> {{ selectedPass.rides_limit }}</div>
              <div><strong>{{ $t('Rides Used') || 'Rides Used' }}:</strong> {{ selectedPass.rides_used }}</div>
              <div><strong>{{ $t('Rides Remaining') || 'Rides Remaining' }}:</strong> {{ selectedPass.rides_remaining }}</div>
            </v-col>

            <!-- Pricing Snapshot -->
            <v-col cols="12" md="6" class="py-2">
              <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-1">
                <v-icon size="16" class="me-1">mdi-currency-usd</v-icon>
                {{ $t('Pricing Snapshot') || 'Pricing Snapshot' }}
              </div>
              <v-divider class="mb-2"></v-divider>
              <div><strong>{{ $t('Single Ride Fare') || 'Single Ride Fare' }}:</strong> {{ selectedPass.single_ride_fare_snapshot }}</div>
              <div><strong>{{ $t('Discount Percent') || 'Discount Percent' }}:</strong> {{ selectedPass.discount_percent_snapshot }}%</div>
              <div><strong>{{ $t('Subtotal') || 'Subtotal Price' }}:</strong> {{ selectedPass.subtotal_price }}</div>
              <div><strong>{{ $t('Discount Amount') || 'Discount Amount' }}:</strong> {{ selectedPass.discount_amount }}</div>
              <div><strong>{{ $t('Total Price') || 'Total Price' }}:</strong> <span class="text-success font-weight-bold">{{ selectedPass.total_price }}</span></div>
            </v-col>

            <!-- Route Details -->
            <v-col cols="12" class="py-2">
              <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-1">
                <v-icon size="16" class="me-1">mdi-road-variant</v-icon>
                {{ $t('Route & Stations') || 'Route & Stations' }}
              </div>
              <v-divider class="mb-2"></v-divider>
              <v-row>
                <v-col cols="12" sm="6">
                  <div><strong>{{ $t('Route Name') || 'Route Name' }}:</strong> {{ selectedPass.route ? selectedPass.route.name : '-' }}</div>
                  <div><strong>{{ $t('Trip ID') || 'Trip ID' }}:</strong> {{ selectedPass.trip ? selectedPass.trip.id : '-' }}</div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div>
                    <v-icon size="14" color="success">mdi-map-marker</v-icon>
                    <strong>{{ $t('Pickup Stop') || 'Pickup Stop' }}:</strong> {{ selectedPass.pickup_stop ? selectedPass.pickup_stop.name : '-' }}
                  </div>
                  <div class="mt-1">
                    <v-icon size="14" color="error">mdi-map-marker-check</v-icon>
                    <strong>{{ $t('Dropoff Stop') || 'Dropoff Stop' }}:</strong> {{ selectedPass.dropoff_stop ? selectedPass.dropoff_stop.name : '-' }}
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <!-- Recent Usages -->
            <v-col cols="12" class="py-2">
              <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-1">
                <v-icon size="16" class="me-1">mdi-format-list-bulleted</v-icon>
                {{ $t('Latest Usages (Max 5)') || 'Latest Usages (Max 5)' }}
              </div>
              <v-divider class="mb-2"></v-divider>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">ID</th>
                      <th class="text-left">{{ $t('Used At') || 'Used At' }}</th>
                      <th class="text-left">{{ $t('Planned Date') || 'Planned Date' }}</th>
                      <th class="text-left">{{ $t('Reservation ID') || 'Reservation ID' }}</th>
                      <th class="text-left">{{ $t('Status') || 'Status' }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="usage in selectedPass.latest_usages" :key="usage.id">
                      <td>{{ usage.id }}</td>
                      <td>{{ usage.used_at }}</td>
                      <td>{{ usage.planned_trip ? usage.planned_trip.planned_date : '-' }}</td>
                      <td>{{ usage.reservation_id || '-' }}</td>
                      <td>
                        <v-chip :color="usage.status === 'used' ? 'success' : 'grey'" x-small dark>
                          {{ usage.status }}
                        </v-chip>
                      </td>
                    </tr>
                    <tr v-if="!selectedPass.latest_usages || selectedPass.latest_usages.length === 0">
                      <td colspan="5" class="text-center py-2 text--secondary">
                        {{ $t('No usage logs found') || 'No usage logs found' }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pb-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="detailsDialog = false">
            {{ $t('Close') || 'Close' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Usages Modal -->
    <v-dialog v-model="usagesDialog" max-width="950px">
      <v-card>
        <v-card-title class="headline success white--text py-3">
          <v-icon dark class="me-2">mdi-history</v-icon>
          {{ $t('Pass Usage History') || 'Usage History' }}
          <v-spacer></v-spacer>
          <span class="text-subtitle-1 font-weight-bold white--text" v-if="selectedPass">{{ selectedPass.pass_code }}</span>
          <v-btn icon dark small class="ms-4" @click="usagesDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-data-table
            :headers="usageHeaders"
            :items="usages"
            :options.sync="usageOptions"
            :server-items-length="totalUsages"
            :loading="isUsagesLoading"
            :loading-text="$t('loading_text')"
            :no-data-text="$t('No usage logs found')"
            :footer-props="footerProps"
          >
            <!-- Usage ID -->
            <template v-slot:item.usage_id="{ item }">
              <span class="font-weight-bold">#{{ item.usage_id }}</span>
            </template>

            <!-- Planned Trip Date -->
            <template v-slot:item.planned_date="{ item }">
              <span v-if="item.planned_trip">{{ item.planned_trip.planned_date }}</span>
              <span v-else class="text--secondary">-</span>
            </template>

            <!-- Segment -->
            <template v-slot:item.segment="{ item }">
              <div v-if="item.pickup_stop_name && item.dropoff_stop_name" class="text-caption">
                <span class="text-success font-weight-semibold">{{ item.pickup_stop_name }}</span>
                <v-icon size="12" class="mx-1">mdi-arrow-right</v-icon>
                <span class="text-error font-weight-semibold">{{ item.dropoff_stop_name }}</span>
              </div>
              <span v-else class="text--secondary">-</span>
            </template>

            <!-- Used At -->
            <template v-slot:item.used_at="{ item }">
              <span>{{ item.used_at }}</span>
            </template>

            <!-- Reservation summary -->
            <template v-slot:item.reservation="{ item }">
              <div v-if="item.reservation_summary" class="text-caption">
                <div><strong>ID:</strong> #{{ item.reservation_summary.id }}</div>
                <div>
                  <strong>{{ $t('Paid') || 'Paid' }}:</strong> {{ item.reservation_summary.paid_price }}
                  <span class="text--secondary">({{ item.reservation_summary.payment_method_label }})</span>
                </div>
                <div><strong>{{ $t('Ride Status') || 'Ride' }}:</strong> {{ item.reservation_summary.ride_status }}</div>
              </div>
              <span v-else class="text--secondary">-</span>
            </template>

            <!-- Status -->
            <template v-slot:item.status="{ item }">
              <v-chip :color="item.status === 'used' ? 'success' : 'grey'" x-small dark>
                {{ item.status }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pb-4">
          <v-spacer></v-spacer>
          <v-btn color="success" outlined @click="usagesDialog = false">
            {{ $t('Close') || 'Close' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'RouteSubscriptionPasses',
  data() {
    return {
      passes: [],
      totalPasses: 0,
      isLoading: false,
      options: {},

      plans: [],
      routes: [],

      filters: {
        user_search: "",
        status: null,
        payment_status: null,
        computed_status: null,
        plan_id: null,
        route_id: null,
        date_from: "",
        date_to: ""
      },

      statusOptions: [
        { text: 'Pending', value: 'pending' },
        { text: 'Active', value: 'active' },
        { text: 'Expired', value: 'expired' },
        { text: 'Cancelled', value: 'cancelled' }
      ],

      paymentStatusOptions: [
        { text: 'Pending', value: 'pending' },
        { text: 'Paid', value: 'paid' },
        { text: 'Failed', value: 'failed' },
        { text: 'Refunded', value: 'refunded' }
      ],

      // Details Modal
      detailsDialog: false,
      selectedPass: null,

      // Usages Modal
      usagesDialog: false,
      usages: [],
      totalUsages: 0,
      isUsagesLoading: false,
      usageOptions: {},

      headers: [
        { text: "Pass Code", value: "pass_code", sortable: false },
        { text: "Customer", value: "customer", sortable: false },
        { text: "Plan", value: "plan", sortable: false },
        { text: "Route", value: "route", sortable: false },
        { text: "Segment", value: "segment", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Payment Status", value: "payment_status", sortable: false },
        { text: "Computed Status", value: "computed_status", sortable: false },
        { text: "Rides Used / Limit", value: "rides_used_limit", sortable: false },
        { text: "Rides Remaining", value: "rides_remaining", sortable: false },
        { text: "Total Price", value: "total_price", sortable: false },
        { text: "Start Date", value: "starts_at", sortable: false },
        { text: "End Date", value: "ends_at", sortable: false },
        { text: "Actions", value: "actions", sortable: false }
      ],

      usageHeaders: [
        { text: "Usage ID", value: "usage_id", sortable: false },
        { text: "Planned Date", value: "planned_date", sortable: false },
        { text: "Segment (Stops)", value: "segment", sortable: false },
        { text: "Used At", value: "used_at", sortable: false },
        { text: "Reservation Summary", value: "reservation", sortable: false },
        { text: "Status", value: "status", sortable: false }
      ]
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(h => ({
        ...h,
        text: this.$t(h.text) || h.text
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page") || "Rows per page"
      };
    }
  },
  watch: {
    options: {
      handler() {
        this.loadPasses();
      },
      deep: true
    },
    usageOptions: {
      handler() {
        if (this.selectedPass && this.usagesDialog) {
          this.loadUsages(this.selectedPass.id);
        }
      },
      deep: true
    }
  },
  mounted() {
    this.fetchFilterOptions();
  },
  methods: {
    fetchFilterOptions() {
      // Fetch Plans
      axios.get('/route-subscription-pass-plans')
        .then(res => {
          this.plans = res.data.map(p => ({
            id: p.id,
            name: this.$i18n.locale === 'ar' && p.name_ar ? p.name_ar : p.name_en
          }));
        })
        .catch(err => {
          console.error("Error fetching plans", err);
        });

      // Fetch Routes
      axios.get('/routes/all')
        .then(res => {
          this.routes = res.data;
        })
        .catch(err => {
          console.error("Error fetching routes", err);
        });
    },

    loadPasses() {
      this.isLoading = true;
      const { page, itemsPerPage } = this.options;

      const params = {
        page: page || 1,
        per_page: itemsPerPage || 15,
        ...this.filters
      };

      axios.get('/admin/route-subscription-passes', { params })
        .then(res => {
          if (res.data.success) {
            this.passes = res.data.data.data;
            this.totalPasses = res.data.data.total;
          } else {
            this.showErrorNotification(res.data.message || "Failed to load passes");
          }
        })
        .catch(err => {
          console.error(err);
          this.showErrorNotification("Error loading passes");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onFilterChange() {
      // Reset to page 1 on filter changes
      if (this.options.page !== 1) {
        this.options.page = 1;
      } else {
        this.loadPasses();
      }
    },

    resetFilters() {
      this.filters = {
        user_search: "",
        status: null,
        payment_status: null,
        computed_status: null,
        plan_id: null,
        route_id: null,
        date_from: "",
        date_to: ""
      };
      this.onFilterChange();
    },

    showDetails(pass) {
      this.selectedPass = null;
      axios.get(`/admin/route-subscription-passes/${pass.id}`)
        .then(res => {
          if (res.data.success) {
            this.selectedPass = res.data.data;
            this.detailsDialog = true;
          } else {
            this.showErrorNotification(res.data.message || "Failed to retrieve pass details");
          }
        })
        .catch(err => {
          console.error(err);
          this.showErrorNotification("Error loading pass details");
        });
    },

    showUsages(pass) {
      this.selectedPass = pass;
      this.usages = [];
      this.totalUsages = 0;
      this.usagesDialog = true;
      
      // Reset usages table options to page 1
      this.usageOptions = {
        page: 1,
        itemsPerPage: 15
      };
      
      this.loadUsages(pass.id);
    },

    loadUsages(passId) {
      this.isUsagesLoading = true;
      const { page, itemsPerPage } = this.usageOptions;

      const params = {
        page: page || 1,
        per_page: itemsPerPage || 15
      };

      axios.get(`/admin/route-subscription-passes/${passId}/usages`, { params })
        .then(res => {
          if (res.data.success) {
            this.usages = res.data.data.data;
            this.totalUsages = res.data.data.total;
          } else {
            this.showErrorNotification(res.data.message || "Failed to retrieve usages");
          }
        })
        .catch(err => {
          console.error(err);
          this.showErrorNotification("Error loading usage history");
        })
        .finally(() => {
          this.isUsagesLoading = false;
        });
    },

    getStatusText(status) {
      switch (status) {
        case 'pending': return 'Pending';
        case 'active': return 'Active';
        case 'expired': return 'Expired';
        case 'cancelled': return 'Cancelled';
        default: return status;
      }
    },

    getStatusColor(status) {
      switch (status) {
        case 'pending': return 'warning';
        case 'active': return 'success';
        case 'expired': return 'error';
        case 'cancelled': return 'grey';
        default: return 'grey';
      }
    },

    getPaymentStatusText(pStatus) {
      switch (pStatus) {
        case 'pending': return 'Pending';
        case 'paid': return 'Paid';
        case 'failed': return 'Failed';
        case 'refunded': return 'Refunded';
        default: return pStatus;
      }
    },

    getPaymentStatusColor(pStatus) {
      switch (pStatus) {
        case 'pending': return 'warning';
        case 'paid': return 'success';
        case 'failed': return 'error';
        case 'refunded': return 'info';
        default: return 'grey';
      }
    },

    showErrorNotification(text) {
      this.$notify({
        title: this.$t("Error") || "Error",
        text: text,
        type: 'error'
      });
    }
  }
};
</script>

<style scoped>
.text-mono {
  font-family: monospace;
}
.font-weight-semibold {
  font-weight: 500;
}
</style>
